import { useState } from "react";
import CustomModal from "Components/Common/CustomModal/CustomModal";
import Button from "Components/Common/Button/Button";
import ChipEmailInput from "Components/Common/ChipEmailInput/ChipEmailInput";
import { EMAIL_REGEX } from "utils/Common/Constant";

interface Props {
  show: boolean;
  title: string;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (emails: string[]) => void;
}

const ShareReportModal = ({
  show,
  title,
  loading = false,
  onClose,
  onConfirm,
}: Props) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [pendingText, setPendingText] = useState("");
  const [hasError, setHasError] = useState(false);

  const handleClose = () => {
    setEmails([]);
    setPendingText("");
    setHasError(false);
    onClose();
  };

  // A typed-but-not-yet-chipped email (Enter never pressed) should still
  // count as a recipient when the user just wants to share to one address.
  const pending = pendingText.trim();
  const pendingIsValidEmail =
    EMAIL_REGEX.test(pending) && !emails.includes(pending);
  const finalEmails = pendingIsValidEmail ? [...emails, pending] : emails;

  const handleConfirm = () => {
    if (!finalEmails.length) return;
    onConfirm(finalEmails);
  };

  return (
    <CustomModal
      show={show}
      handleClose={handleClose}
      title={title}
      isCrossBtn
      footerContent={
        <>
          <Button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={loading || hasError || !finalEmails.length}
          >
            {loading ? (
              <>
                <div className="btn-spinner" /> Sharing…
              </>
            ) : (
              "Share"
            )}
          </Button>
        </>
      }
    >
      <p>
        Enter the email addresses to share this report with. Press Enter to add
        multiple recipients.
      </p>
      <ChipEmailInput
        value={emails}
        onChange={setEmails}
        onErrorChange={setHasError}
        onInputTextChange={setPendingText}
        placeholder="Type an email and press Enter to add more"
      />
    </CustomModal>
  );
};

export default ShareReportModal;
